// ── Teacher availability / conflict checking ──────────────────────────────
// Pure client-side Supabase queries, alongside the existing meetHelper.js
// pattern — this codebase uses zero RPCs anywhere, staying consistent.
//
// Two independent checks combine into one verdict:
//   - withinAvailability: is the requested slot inside a window the teacher
//     is actually meant to be working, per teacher_availability?
//   - conflicts: does it overlap one of the teacher's other existing
//     classes on that date?
//
// Product decision (confirmed): a teacher with zero configured
// availability rows is NOT blocked — fails open so newly-added teachers
// aren't silently locked out before admin gets around to setting their
// hours. Real class-vs-class conflicts always come back non-empty
// regardless, since that's independent of configuration.

import { supabase } from './supabaseClient'
import { dayOfWeekIST, toMinutes, minutesToHHMM } from './timeHelpers'

export const DEFAULT_DURATION_MINUTES = 60
export const AVAILABILITY_RESTRICTIONS_ENABLED = false

export async function getTeacherAvailability(teacherId) {
  if (!teacherId) return []
  const { data, error } = await supabase
    .from('teacher_availability')
    .select('*')
    .eq('teacher_id', teacherId)
    .eq('is_active', true)
  return error ? [] : (data || [])
}

// Pure function — is [startTime, startTime+duration) fully inside any of
// the teacher's windows for that weekday?
export function isWithinAvailability(availabilityRows, dayOfWeek, startTime, durationMinutes = DEFAULT_DURATION_MINUTES) {
  if (!AVAILABILITY_RESTRICTIONS_ENABLED) return true
  if (!availabilityRows || !availabilityRows.length) return true // fail-open — not configured yet
  const reqStart = toMinutes(startTime)
  const reqEnd   = reqStart + durationMinutes
  return availabilityRows
    .filter(r => r.day_of_week === dayOfWeek)
    .some(r => toMinutes(r.start_time) <= reqStart && reqEnd <= toMinutes(r.end_time))
}

// Existing-class collisions for a teacher on a given date. excludeClassId
// matters for reschedules — a class shouldn't conflict with its own
// current slot while being moved.
export async function getTeacherConflicts({ teacherId, date, startTime, durationMinutes = DEFAULT_DURATION_MINUTES, excludeClassId = null }) {
  if (!teacherId || !date || !startTime) return []
  let q = supabase.from('classes')
    .select('id,title,start_time,duration_minutes,class_date')
    .eq('teacher_id', teacherId)
    .eq('class_date', date)
    .eq('is_cancelled', false)
  if (excludeClassId) q = q.neq('id', excludeClassId)
  const { data, error } = await q
  if (error || !data) return []

  const reqStart = toMinutes(startTime)
  const reqEnd   = reqStart + durationMinutes
  return data.filter(c => {
    const cStart = toMinutes(c.start_time)
    const cEnd   = cStart + (c.duration_minutes || DEFAULT_DURATION_MINUTES)
    return reqStart < cEnd && cStart < reqEnd // standard interval-overlap test
  })
}

// For bulk operations (up to 500 dates in one ScheduleClasses.js batch) —
// one query for the whole date range instead of one round-trip per date.
export async function getTeacherConflictsInRange({ teacherId, fromDate, toDate }) {
  if (!teacherId || !fromDate || !toDate) return []
  const { data, error } = await supabase.from('classes')
    .select('id,title,class_date,start_time,duration_minutes')
    .eq('teacher_id', teacherId)
    .eq('is_cancelled', false)
    .gte('class_date', fromDate)
    .lte('class_date', toDate)
  return error ? [] : (data || [])
}

// Pure function pairing with getTeacherConflictsInRange — does this one
// date/time/duration overlap anything already fetched for that date?
export function hasConflictOnDate(conflictRows, date, startTime, durationMinutes = DEFAULT_DURATION_MINUTES) {
  const reqStart = toMinutes(startTime)
  const reqEnd   = reqStart + durationMinutes
  return conflictRows.filter(c => c.class_date === date).some(c => {
    const cStart = toMinutes(c.start_time)
    const cEnd   = cStart + (c.duration_minutes || DEFAULT_DURATION_MINUTES)
    return reqStart < cEnd && cStart < reqEnd
  })
}

// Main entry point every scheduling/reschedule flow should call before
// saving. Returns { ok, withinAvailability, conflicts, reason }.
export async function checkTeacherAvailability({ teacherId, date, startTime, durationMinutes = DEFAULT_DURATION_MINUTES, excludeClassId = null }) {
  if (!teacherId || !date || !startTime) return { ok: true, withinAvailability: true, conflicts: [] }

  const dow  = dayOfWeekIST(date)
  const rows = await getTeacherAvailability(teacherId)
  const withinAvailability = isWithinAvailability(rows, dow, startTime, durationMinutes)
  const conflicts = await getTeacherConflicts({ teacherId, date, startTime, durationMinutes, excludeClassId })

  return {
    ok: withinAvailability && conflicts.length === 0,
    withinAvailability,
    conflicts,
    reason: conflicts.length
      ? `Overlaps ${conflicts.length === 1 ? 'an existing class' : `${conflicts.length} existing classes`} (${conflicts.map(c => c.title).join(', ')})`
      : !withinAvailability
        ? "Outside this teacher's declared working hours"
        : null,
  }
}

// For the slot-picker UI — status per slot on one date, stepping by
// slotStepMinutes across every availability window the teacher has that
// weekday. Only ever returns slots the teacher is actually meant to be
// working (no "outside hours" entries here — that distinction is purely
// for scheduling-time warnings, not slot browsing).
export async function getAvailableSlotsForDate({ teacherId, date, durationMinutes = DEFAULT_DURATION_MINUTES, slotStepMinutes = 30 }) {
  if (!teacherId || !date) return []
  const dow  = dayOfWeekIST(date)
  const rows = (await getTeacherAvailability(teacherId)).filter(r => r.day_of_week === dow)
  if (!rows.length) return []

  const conflicts = await getTeacherConflicts({ teacherId, date, startTime: '00:00', durationMinutes: 24 * 60 })
  const slots = []
  for (const win of rows) {
    const winStart = toMinutes(win.start_time)
    const winEnd   = toMinutes(win.end_time)
    for (let m = winStart; m + durationMinutes <= winEnd; m += slotStepMinutes) {
      const isBooked = conflicts.some(c => {
        const cStart = toMinutes(c.start_time)
        const cEnd   = cStart + (c.duration_minutes || DEFAULT_DURATION_MINUTES)
        return m < cEnd && cStart < m + durationMinutes
      })
      slots.push({ time: minutesToHHMM(m), status: isBooked ? 'booked' : 'available' })
    }
  }
  return slots.sort((a, b) => a.time.localeCompare(b.time))
}

// For quick day-coloring in a month grid — does the teacher have ANY
// availability window on this weekday at all (no DB call, pure lookup).
export function hasAvailabilityOnWeekday(availabilityRows, dayOfWeek) {
  return (availabilityRows || []).some(r => r.day_of_week === dayOfWeek)
}
